import React, { useLayoutEffect, useState } from 'react'
import { KeyValuePair } from '@toasttab/shared/src/interfaces/Interface.d'
import {
  FormCategory,
  FieldInputOption,
  FormFieldCategory
} from '@toasttab/shared/src/interfaces/Forms.d'
import { useFormik } from 'formik'
import {
  SelectInput,
  CheckboxInput,
  Button,
  RadioInput
} from '@toasttab/shared/src/components'
import FormField from '@toasttab/shared/src/components/atoms/Forms/FormField/FormField'
import TextInput from '@toasttab/shared/src/components/atoms/Forms/TextInput/TextInput'
import { captureException } from '@sentry/gatsby'
import _helpers from '@toasttab/shared/src/globals/_helpers'
import { getInitialFormValues } from '@toasttab/shared/src/hooks/useDataCapture'
import { extractFirstNameLastName } from '@toasttab/shared/src/utils/dataCaptureUtils'
import { useGeoLocation } from '@toasttab/shared/src/hooks/useGeoLocation'
import { postForm } from '@toasttab/shared/src/utils/formUtils'
import PrivacyStatement from '../../Common/PrivacyStatement'
import HiddenFields from '../HiddenFields'
import DateInput from '@toasttab/shared/src/components/atoms/Forms/DateInput/DateInput'
import { useI18next } from '@toasttab/shared/src/hooks/useI18next'
import Sites from '@toasttab/shared/src/data/Sites.json'

interface MainFormProps {
  entryId: string
  typeHandle: string
  formId: string
  formCategory: FormCategory
  programName: string
  redirectUrl: string
  buttonText: string
  buttonStyle?:
    | 'primary'
    | 'secondary'
    | 'secondary-otl'
    | 'secondary-alt-otl'
    | 'text'
  conversionType: string
  hidePrivacy?: boolean
  mds?: boolean
}

const MainForm = ({
  entryId,
  typeHandle,
  formId,
  formCategory,
  programName,
  redirectUrl,
  buttonText,
  buttonStyle = 'primary',
  conversionType,
  hidePrivacy = false,
  mds = false
}: MainFormProps) => {
  const { language } = useI18next()
  const activeSite = Sites.sites.filter(
    (site) => site.regionCode === language
  )[0]

  const [isSubmitting, setSubmitting] = useState(false)
  const [isProgressiveFormActivated, setProgressiveForm] = useState(false)
  const [isSubmitted, setSubmitted] = useState(false)
  const [isError, setError] = useState(false)
  const [postalCode, setPostalCode] = useState(null)
  const [countryCode, setCountryCode] = useState(null)
  const { geoData, loading } = useGeoLocation()
  let formVariation: 'default' | 'otl-default' = mds ? 'default' : 'otl-default'

  useLayoutEffect(() => {
    if (geoData) {
      setPostalCode(geoData?.postal?.code)
      setCountryCode(geoData?.country?.iso_code)
    }
  }, [loading])

  const progressiveCookie = _helpers.getCookie('progressiveEnabled')
  const isProgressiveForm = progressiveCookie

  let includedCoreFields = [
    'mkoFullName',
    'Email',
    'Phone',
    'Company',
    'country'
  ]
  const emailOnlySlugs = [
    'email-only',
    'ebook',
    'ebook-pos',
    'ebook-nro',
    'ebook-restaurant-type',
    'ebook-restaurant-type-1',
    'innovation-summit-signup',
    'video-training'
  ]
  if (formCategory && emailOnlySlugs.includes(formCategory.slug)) {
    includedCoreFields =
      language === 'en' ? ['Email'] : ['Email', 'country', 'optIn']
  }

  conversionType =
    formCategory && formCategory.slug === 'email-only' ? 'OT' : conversionType
  // dynamically create initialValues for Formik
  let initValues: KeyValuePair = {
    craftRestaurantToast: '', // honeypot, purposefully empty, if this is filled then assuming it's bot
    munchkinId: `${process.env.GATSBY_FORM_MUNCHKINID ?? '713-DII-842'}`,
    formid: formId,
    returnURL: `${language !== 'en' ? '/' + language : ''}${redirectUrl}`,
    eI: `${entryId}`,
    browserLanguage: 'en',
    referrerURL: `${process.env.GATSBY_CRAFT_URL}${
      typeof window !== 'undefined' && window.location.pathname
    }`,
    _mktoReferrer: `${typeof window !== 'undefined' && window.location.href}`,
    _mkt_trk: `${_helpers.getCookie('_mkto_trk')}`,
    mkoProgramName: `${programName}`,
    fC: `${typeHandle}-${entryId}`,
    craftFieldsetSlug: formCategory && formCategory.slug,
    email: _helpers.getCookie('email'),
    mkoFullName: _helpers.getCookie('fullName'),
    phone: _helpers.getCookie('phonenumber'),
    company: _helpers.getCookie('company'),
    referringPageURLText: ``,
    craftJavascriptStatus: `Loaded`,
    shownCalendly: false,
    recaptchaValid: `no`,
    appendVariantTest: ``,
    craftFormLocation: `NOT SET`,
    gae: ``,
    optIn: false
  }

  // set up additional standard initial marketo form values
  const { initialValues } = getInitialFormValues(initValues)
  formCategory?.coreFields?.forEach((f: FormFieldCategory) => {
    // set default value for the radio fields
    if (f.fieldInputType === 'radio') {
      initialValues[f.fieldInputNameMarketoRestApi] =
        f.fieldInputOptions[0].optionValue
    }
    if (f.fieldInputType === 'select') {
      initialValues[f.fieldInputNameMarketoRestApi] = ''
    }
    if (f.fieldInputType === 'select' && f.fieldInputName === 'country') {
      initialValues[f.fieldInputNameMarketoRestApi] = activeSite.siteName
    }
    if (f.fieldInputType === 'date') {
      initialValues[f.fieldInputNameMarketoRestApi] = ''
    }
  })

  formCategory?.progressiveFields?.forEach((f: FormFieldCategory) => {
    // set default value for the radio fields
    if (f.fieldInputType === 'radio') {
      initialValues[f.fieldInputNameMarketoRestApi] =
        f.fieldInputOptions[0].optionValue
    }
    if (f.fieldInputType === 'select') {
      initialValues[f.fieldInputNameMarketoRestApi] = ''
    }
  })
  const validateFormSubmission = (values: KeyValuePair) => {
    const errors: KeyValuePair = {}

    if (!isProgressiveForm) {
      formCategory?.coreFields?.forEach((f: FormFieldCategory) => {
        if (f.fieldRequired && !values[f.fieldInputNameMarketoRestApi]) {
          errors[
            f.fieldInputNameMarketoRestApi
          ] = `${f.fieldInputLabel} is required`
        } else if (f.fieldInputType === 'email') {
          if (_helpers.validateEmail(values[f.fieldInputNameMarketoRestApi])) {
            errors.email = 'Invalid email address'
          } else {
            // @ts-ignore - Typescript doesn't like setting new properties on window
            let heapHandler = window.heap
            if (
              typeof window !== 'undefined' &&
              typeof heapHandler !== 'undefined'
            ) {
              heapHandler.addUserProperties({
                email: values[f.fieldInputNameMarketoRestApi].toLowerCase()
              })
              heapHandler.identify(
                values[f.fieldInputNameMarketoRestApi].toLowerCase()
              )
            }
          }
        } else if (f.fieldInputType === 'tel') {
          // currently otl doesnt validate phone
        } else if (f.fieldInputType === 'date') {
          const min = f.fieldInputOptions[0].optionValue
          const max = f.fieldInputOptions[1].optionValue
          const value = values[f.fieldInputNameMarketoRestApi]
          if (value > max) {
            errors[
              f.fieldInputNameMarketoRestApi
            ] = `value must be ${max} or earlier`
          } else if (value < min) {
            errors[
              f.fieldInputNameMarketoRestApi
            ] = `value must be ${min} or later`
          }
        } else if (f.fieldInputNameMarketoRestApi === 'mkoFullName') {
          const fullNameRegex =
            /^[a-zA-ZÀ-ÖØ-öø-ÿ]+(?:[-'’][a-zA-ZÀ-ÖØ-öø-ÿ]+)*(?: [a-zA-ZÀ-ÖØ-öø-ÿ]+(?:[-'’][a-zA-ZÀ-ÖØ-öø-ÿ]+)*)+$/
          if (!fullNameRegex.test(values[f.fieldInputNameMarketoRestApi])) {
            errors[
              f.fieldInputNameMarketoRestApi
            ] = `${f.fieldInputLabel} is required`
          }
        }
      })
    } else {
      setProgressiveForm(true)
      // Progressive Form
      formCategory?.coreFields
        ?.filter((f: FormFieldCategory) =>
          includedCoreFields.includes(f.fieldInputName)
        )
        .forEach((f: FormFieldCategory) => {
          if (f.fieldRequired && !values[f.fieldInputNameMarketoRestApi]) {
            errors[
              f.fieldInputNameMarketoRestApi
            ] = `${f.fieldInputLabel} is required`
          } else if (f.fieldInputType == 'email') {
            if (
              _helpers.validateEmail(values[f.fieldInputNameMarketoRestApi])
            ) {
              errors.email = 'Invalid email address'
            } else {
              // @ts-ignore - Typescript doesn't like setting new properties on window
              let heapHandler = window.heap
              if (
                typeof window !== 'undefined' &&
                typeof heapHandler !== 'undefined'
              ) {
                heapHandler.addUserProperties({
                  email: values[f.fieldInputNameMarketoRestApi].toLowerCase()
                })
                heapHandler.identify(
                  values[f.fieldInputNameMarketoRestApi].toLowerCase()
                )
              }
            }
          } else if (f.fieldInputType == 'tel') {
            // currently otl doesnt validate phone
          } else if (f.fieldInputType == 'date') {
            const min = f.fieldInputOptions[0].optionValue
            const max = f.fieldInputOptions[1].optionValue
            const value = values[f.fieldInputNameMarketoRestApi]
            if (value > max) {
              errors[
                f.fieldInputNameMarketoRestApi
              ] = `value must be ${max} or earlier`
            } else if (value < min) {
              errors[
                f.fieldInputNameMarketoRestApi
              ] = `value must be ${min} or later`
            }
          }
        })

      formCategory?.progressiveFields?.forEach((f: FormFieldCategory) => {
        if (f.fieldRequired && !values[f.fieldInputNameMarketoRestApi]) {
          errors[
            f.fieldInputNameMarketoRestApi
          ] = `${f.fieldInputLabel} is required`
        } else if (f.fieldInputType == 'email') {
          if (_helpers.validateEmail(values[f.fieldInputNameMarketoRestApi])) {
            errors.email = 'Invalid email address'
          }
        } else if (f.fieldInputType == 'tel') {
          // currently otl doesnt validate phone
        } else if (f.fieldInputType == 'date') {
          const min = f.fieldInputOptions[0].optionValue
          const max = f.fieldInputOptions[1].optionValue
          const value = values[f.fieldInputNameMarketoRestApi]
          if (value > max) {
            errors[
              f.fieldInputNameMarketoRestApi
            ] = `value must be ${max} or earlier`
          } else if (value < min) {
            errors[
              f.fieldInputNameMarketoRestApi
            ] = `value must be ${min} or later`
          }
        }
      })
    }
    return errors
  }

  // Pass the useFormik() hook initial form values and a submit function that will
  // be called when the form is submitted
  const formik = useFormik({
    initialValues: initialValues,
    validate: validateFormSubmission,
    validateOnBlur: true,
    validateOnChange: true,
    onSubmit: (values) => {
      const extractedName = extractFirstNameLastName(values['mkoFullName'])
      values['firstName'] = extractedName.FirstName
      values['lastName'] = extractedName.LastName

      let formData = new FormData()
      formData.append('craftInferredCountry', _helpers.getCountry(countryCode))
      formData.append('postalCode', postalCode)

      for (let value in values) {
        if (value === 'mkoFullName') {
          if (extractedName.FirstName) {
            _helpers.createCookie('fN', extractedName.FirstName, 525600, '/')
          }
          if (extractedName.LastName) {
            _helpers.createCookie('lN', extractedName.LastName, 525600, '/')
          }
          if (extractedName.FullName) {
            _helpers.createCookie(
              'fullName',
              extractedName.FullName,
              525600,
              '/'
            )
          }
        } else if (value === 'email') {
          _helpers.createCookie('email', values[value], 525600, '/')
        } else if (value === 'company') {
          _helpers.createCookie('company', values[value], 525600, '/')
        } else if (value === 'phone') {
          _helpers.createCookie('phonenumber', values[value], 525600, '/')
        } else if (value === 'mkoProgramName') {
          if (
            values[value].toLowerCase() === 'Website-Demo-Request'.toLowerCase()
          ) {
            _helpers.createCookie('toastDemoForm', '1', 525600, '/')
          }
        } else {
          _helpers.createCookie('toastResourceForm', '1', 900, '/')
          _helpers.createCookie('progressiveEnabled', '1', 43200, '/')
        }

        formData.append(value, values[value])

        // Only when Toast Type exists
        if (values['toastType']) {
          if (value === 'returnURL') {
            let returnUrl = '/request-demo-confirmed'
            if (
              values['toastType'] === "I'm already a Toast customer" ||
              values['toastType'] === 'I’m a Toast customer looking for help'
            ) {
              returnUrl = '/thank-you/toast-confirmed?customer=true'
            } else if (
              values['toastType'] === "I'm a restaurant guest" ||
              values['toastType'] === 'I’m a restaurant guest looking for help'
            ) {
              returnUrl = '/thank-you/toast-confirmed?customer=false'
            }
            formData.append(value, returnUrl)
          } else {
            formData.append(value, values[value])
          }
        }
      }

      setSubmitting(true)
      postForm(formData)
        .then((response) => {
          setSubmitted(true)
          if (response.redirected) {
            // @ts-ignore
            // Typescript doesn't like setting a window.location as a string, despite it being common practice
            // https://github.com/microsoft/TypeScript/issues/48949
            window.location = response.url
          }
        })
        .catch(function (err) {
          captureException(
            `${err} - ${JSON.stringify(values, null, 2)} - OTL-gatsby`
          )
          setError(true)
        })
        .finally(() => {
          setTimeout(() => setSubmitting(false), 3000)
        })
    }
  })

  const fillOptions = (fieldLabel: string, options: FieldInputOption[]) => {
    let optionArr: {
      label: string
      value: string
      disabled: boolean
      selected: boolean
    }[] = [
      { label: fieldLabel, value: fieldLabel, disabled: true, selected: false }
    ]
    options.map((o: FieldInputOption) => {
      return optionArr.push({
        label: o.optionLabel,
        value: o.optionValue,
        disabled: false,
        selected: false
      })
    })

    return optionArr
  }

  const fillCountryOptions = (
    fieldLabel: string,
    options: FieldInputOption[]
  ) => {
    let optionArr: {
      label: string
      value: string
      disabled: boolean
      selected: boolean
    }[] = [
      { label: fieldLabel, value: fieldLabel, disabled: true, selected: false }
    ]
    options.map((o: FieldInputOption) => {
      return optionArr.push({
        label: o.optionLabel,
        value: o.optionValue,
        disabled: false,
        selected: o.optionValue === activeSite.siteName
      })
    })

    return optionArr
  }

  const renderFields = (f: FormFieldCategory, i: number) => {
    return (
      <FormField key={i} mds={mds} fullWidth>
        {f.fieldInputType === 'text' && (
          <TextInput
            type='text'
            name={f.fieldInputNameMarketoRestApi}
            id={f.fieldInputName}
            placeholder={f.fieldInputLabel}
            fullWidth
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={formik.values[f.fieldInputNameMarketoRestApi] ?? ''}
            variation={formVariation}
            padding='medium'
            required={f.fieldRequired}
            error={
              formik.touched[f.fieldInputNameMarketoRestApi] &&
              formik.errors[f.fieldInputNameMarketoRestApi]
                ? (formik.errors[f.fieldInputNameMarketoRestApi] as string[])
                : ''
            }
          />
        )}
        {f.fieldInputType === 'email' && (
          <TextInput
            type='email'
            name={f.fieldInputNameMarketoRestApi}
            id={f.fieldInputName}
            placeholder={f.fieldInputLabel}
            fullWidth
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={formik.values[f.fieldInputNameMarketoRestApi] ?? ''}
            variation={formVariation}
            padding='medium'
            required={f.fieldRequired}
            error={
              formik.touched[f.fieldInputNameMarketoRestApi] &&
              formik.errors[f.fieldInputNameMarketoRestApi]
                ? (formik.errors[f.fieldInputNameMarketoRestApi] as string[])
                : ''
            }
          />
        )}
        {f.fieldInputType === 'tel' && (
          <TextInput
            type='tel'
            name={f.fieldInputNameMarketoRestApi}
            id={f.fieldInputName}
            placeholder={f.fieldInputLabel}
            fullWidth
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={formik.values[f.fieldInputNameMarketoRestApi] ?? ''}
            variation={formVariation}
            padding='medium'
            required={f.fieldRequired}
            error={
              formik.touched[f.fieldInputNameMarketoRestApi] &&
              formik.errors[f.fieldInputNameMarketoRestApi]
                ? (formik.errors[f.fieldInputNameMarketoRestApi] as string[])
                : ''
            }
          />
        )}

        {f.fieldInputType === 'select' && f.fieldInputName === 'country' && (
          <SelectInput
            name={f.fieldInputNameMarketoRestApi}
            id={f.fieldInputName}
            label={f.fieldInputLabel}
            options={fillCountryOptions(f.fieldInputLabel, f.fieldInputOptions)}
            fullWidth
            required={f.fieldRequired}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            padding='medium'
            error={
              formik.touched[f.fieldInputNameMarketoRestApi] &&
              formik.errors[f.fieldInputNameMarketoRestApi]
                ? (formik.errors[f.fieldInputNameMarketoRestApi] as string[])
                : ''
            }
          />
        )}
        {f.fieldInputType === 'select' && f.fieldInputName !== 'country' && (
          <SelectInput
            name={f.fieldInputNameMarketoRestApi}
            id={f.fieldInputName}
            label={f.fieldInputLabel}
            options={fillOptions(f.fieldInputLabel, f.fieldInputOptions)}
            value={'United Kingdom'}
            fullWidth
            required={f.fieldRequired}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            padding='medium'
            error={
              formik.touched[f.fieldInputNameMarketoRestApi] &&
              formik.errors[f.fieldInputNameMarketoRestApi]
                ? (formik.errors[f.fieldInputNameMarketoRestApi] as string[])
                : ''
            }
          />
        )}
        {f.fieldInputType === 'checkbox' && (
          <CheckboxInput
            id={`${f.id}${f.fieldInputName}`}
            name={f.fieldInputNameMarketoRestApi}
            labels={f.fieldInputOptions}
            label={f.fieldInputOptions && f.fieldInputOptions[0].optionLabel}
            onChange={formik.handleChange}
            value={formik.values[f.fieldInputNameMarketoRestApi] ?? ''}
            variation='default'
          />
        )}

        {f.fieldInputType === 'radio' && (
          <RadioInput
            id={f.fieldInputName}
            name={f.fieldInputNameMarketoRestApi}
            label={f.fieldInputLabel}
            options={f.fieldInputOptions.map((o: FieldInputOption) => ({
              label: o.optionLabel,
              value: o.optionValue
            }))}
            onChange={formik.handleChange}
            value={formik.values[f.fieldInputNameMarketoRestApi] ?? ''}
            variation={formVariation}
          />
        )}

        {f.fieldInputType === 'date' && (
          <DateInput
            variation={formVariation}
            id={f.fieldInputName}
            name={f.fieldInputNameMarketoRestApi}
            label={f.fieldInputLabel}
            min={f.fieldInputOptions[0].optionValue}
            max={f.fieldInputOptions[1].optionValue}
            required={f.fieldRequired}
            value={formik.values[f.fieldInputNameMarketoRestApi] ?? ''}
            error={
              formik.touched[f.fieldInputNameMarketoRestApi] &&
              formik.errors[f.fieldInputNameMarketoRestApi]
                ? (formik.errors[f.fieldInputNameMarketoRestApi] as string[])
                : ''
            }
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          />
        )}
      </FormField>
    )
  }
  if (loading) return <></>

  if ((!isSubmitting || isProgressiveFormActivated) && isProgressiveForm) {
    return (
      <>
        <div className='mb-4'>
          <form
            id={`mktForm_${formId}`}
            data-heap-mktform-id={formId}
            onSubmit={(e) => {
              e.preventDefault()
              if (formik.isValid) {
                formik.submitForm()
              }
            }}
            data-ct={conversionType}
            data-testid={isSubmitted ? 'form-submitted' : 'form-not-submitted'}
          >
            {formCategory &&
              formCategory.coreFields &&
              formCategory.coreFields
                .filter((f: FormFieldCategory) =>
                  includedCoreFields.includes(f.fieldInputName)
                )
                .map((f: FormFieldCategory, i: number) => {
                  return renderFields(f, i)
                })}
            {formCategory &&
              formCategory.progressiveFields &&
              formCategory.progressiveFields.map(
                (f: FormFieldCategory, i: number) => {
                  return renderFields(f, i)
                }
              )}
            <HiddenFields
              formik={formik}
              countryCode={countryCode}
              postalCode={postalCode}
            />
            <Button
              type='submit'
              disabled={isSubmitting}
              arrow={false}
              classes={`${
                buttonStyle === 'secondary-otl'
                  ? 'hover:border-toast-blue-700 hover:bg-toast-blue-700'
                  : ''
              } ${isSubmitting ? 'after:invisible' : ''}`}
              fullwidth
              variation={buttonStyle}
            >
              {isSubmitting && (
                <div className='absolute top-[50%] left-[50%] translate-x-[-50%] translate-y-[-50%] '>
                  <img
                    src='https://toast-craft-assets.s3.amazonaws.com/spinner.gif'
                    className='w-[43px] h-[43px] grayscale'
                    alt='loadingspinner'
                  />
                </div>
              )}
              <div className={isSubmitting ? 'invisible' : 'font-semibold'}>
                {buttonText}
              </div>
            </Button>
          </form>
        </div>
        {!hidePrivacy && <PrivacyStatement center />}
      </>
    )
  }

  return (
    <>
      <div className='mb-4'>
        <form
          id={`mktForm_${formId}`}
          data-heap-mktform-id={formId}
          onSubmit={(e) => {
            e.preventDefault()
            if (formik.isValid) {
              formik.submitForm()
            }
          }}
          aria-label='Main Form'
          data-ct={conversionType}
          data-testid={isSubmitted ? 'form-submitted' : 'form-not-submitted'}
        >
          {formCategory &&
            formCategory.coreFields &&
            formCategory.coreFields.map((f: FormFieldCategory, i: number) => {
              return renderFields(f, i)
            })}

          <HiddenFields
            formik={formik}
            countryCode={countryCode}
            postalCode={postalCode}
          />
          <Button
            type='submit'
            disabled={isSubmitting}
            classes={`${
              buttonStyle === 'secondary-otl'
                ? 'hover:border-toast-blue-700 hover:bg-toast-blue-700'
                : ''
            } ${isSubmitting ? 'after:invisible' : ''}`}
            arrow={false}
            fullwidth
            variation={buttonStyle}
          >
            {isSubmitting && (
              <div className='absolute top-[50%] left-[50%] translate-x-[-50%] translate-y-[-50%] '>
                <img
                  src='https://toast-craft-assets.s3.amazonaws.com/spinner.gif'
                  className='w-[43px] h-[43px] grayscale'
                  alt='loadingspinner'
                />
              </div>
            )}
            <div className={isSubmitting ? 'invisible' : 'font-semibold'}>
              {buttonText}
            </div>
          </Button>
        </form>
        {isError && (
          <div className='text-otl-error text-xs relative text-left top-[-15px]'>
            There was an error trying to submit the form.
          </div>
        )}
      </div>
      <PrivacyStatement center />
    </>
  )
}

export default MainForm
